import { useState, useEffect, useRef } from 'react';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { isToday } from 'date-fns';
import { 
  LiveKitRoom, 
  RoomAudioRenderer,
  useTracks,
  VideoTrack,
  TrackRefContext,
  useLocalParticipant,
  useRoomContext
} from '@livekit/components-react';
import { Track, LocalParticipant } from 'livekit-client';
import '@livekit/components-styles';
import { toLiveKitRoomName, type CallOutcome } from '@/lib/clinic/callSession';
import { useLiveKitConnection } from './useLiveKitConnection';
import { LiveKitConnectionWatcher } from './LiveKitConnectionWatcher';

// Utility function to reset favicon
const resetFavicon = () => {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  if (link) {
    link.href = '/namaticon.png';
  }
  document.title = 'Namat Platform  I  منصة نمط';
};

interface FloatingVideoCallProps {
  roomId: string;
  userRole: 'customer' | 'coach';
  userName: string;
  otherParticipantName?: string;
  /** `outcome.connected` is false when the other party never joined (#47). */
  onCallEnded: (durationMinutes: number, outcome?: CallOutcome) => void;
  open: boolean;
  appointmentDate?: Date;
  forceFullscreen?: boolean;
  /** Clinic calls: log each attempt to `callAttempts` (#47). */
  attempt?: { coachId: string; customerId: string };
  /** Called once when the other party first joins the room. */
  onRemoteJoined?: () => void;
}

// Video controls component (inside LiveKitRoom context)
function VideoControls({ onEndCall }: { onEndCall: () => void }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);

  const toggleVideo = async () => {
    if (localParticipant) {
      await localParticipant.setCameraEnabled(!videoEnabled);
      setVideoEnabled(!videoEnabled);
    }
  };

  const toggleAudio = async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(!audioEnabled);
      setAudioEnabled(!audioEnabled);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={toggleVideo}
        className={`rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-lg w-10 h-10 md:w-12 md:h-12 ${
          videoEnabled 
            ? 'bg-white/20 hover:bg-white/30 text-white' 
            : 'bg-red-500/80 hover:bg-red-500/60 text-white'
        }`}
      >
        {videoEnabled ? <Video className="h-4 w-4 md:h-5 md:w-5" /> : <VideoOff className="h-4 w-4 md:h-5 md:w-5" />}
      </button>
      
      <button
        onClick={toggleAudio}
        className={`rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-lg w-10 h-10 md:w-12 md:h-12 ${
          audioEnabled 
            ? 'bg-white/20 hover:bg-white/30 text-white' 
            : 'bg-red-500/80 hover:bg-red-500/60 text-white'
        }`}
      >
        {audioEnabled ? <Mic className="h-4 w-4 md:h-5 md:w-5" /> : <MicOff className="h-4 w-4 md:h-5 md:w-5" />}
      </button>

      <button
        onClick={onEndCall}
        className="rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center transition-colors cursor-pointer shadow-lg w-10 h-10 md:w-12 md:h-12"
      >
        <PhoneOff className="h-4 w-4 md:h-5 md:w-5" />
      </button>
    </div>
  );
}

// Custom video layout component
function CustomVideoLayout({ 
  otherParticipantName, 
  language,
  dir,
  isMobile,
  forceFullscreen,
  onEndCall,
  userRole
}: { 
  otherParticipantName?: string; 
  language: string;
  dir: string;
  isMobile: boolean;
  forceFullscreen: boolean;
  onEndCall: () => void;
  userRole: 'customer' | 'coach';
}) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  
  const { localParticipant } = useLocalParticipant();

  const localVideoTrack = tracks.find(
    (track) => track.participant.sid === localParticipant.sid && track.source === Track.Source.Camera
  );
  
  const remoteVideoTracks = tracks.filter(
    (track) => {
      const isRemote = track.participant.sid !== localParticipant.sid;
      const isCamera = track.source === Track.Source.Camera;
      const hasPublication = !!track.publication;
      const hasTrack = !!track.publication?.track;
      return isRemote && isCamera && hasPublication && hasTrack;
    }
  );

  const hasRemoteVideo = remoteVideoTracks.length > 0;

  return (
    <div className="relative w-full h-full">
      {/* Remote video - full background */}
      {hasRemoteVideo ? (
        <div className="absolute inset-0">
          <TrackRefContext.Provider value={remoteVideoTracks[0]}>
            <VideoTrack 
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 30%' }}
            />
          </TrackRefContext.Provider>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <div className={`w-16 h-16 mx-auto ${userRole === 'coach' ? 'bg-gray-500/50' : 'bg-gray-700'} rounded-full flex items-center justify-center ${userRole === 'coach' ? '' : 'mb-4'}`}>
              <Video className={`h-8 w-8 ${userRole === 'coach' ? 'text-gray-300' : 'text-gray-400'}`} />
            </div>
            {userRole !== 'coach' && (
              <>
                <p className="text-sm font-medium mb-1">
                  {language === 'ar' ? 'في انتظار انضمام...' : 'Waiting to join...'}
                </p>
                <p className="text-xs text-gray-400">{otherParticipantName}</p>
                <div className="flex items-center justify-center gap-1 mt-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header Info */}
      <div className={`absolute top-2 ${dir === 'rtl' ? 'right-2' : 'left-2'} bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 text-white z-20`}>
        <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
          <span className="text-xs font-medium">
            {hasRemoteVideo 
              ? `${language === 'ar' ? 'متصل مع' : 'Connected with'} ${otherParticipantName}`
              : `${language === 'ar' ? 'في انتظار' : 'Waiting for'} ${otherParticipantName}`
            }
          </span>
          <div className={`w-2 h-2 rounded-full ${hasRemoteVideo ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`}></div>
        </div>
      </div>

      {/* Local video PiP - on opposite side from controls */}
      {localVideoTrack && (
        <div 
          className={`absolute ${dir === 'rtl' ? 'right-2' : 'left-2'} bg-gray-800 rounded-lg overflow-hidden border border-white/20 z-10`}
          style={{ 
            width: isMobile ? `${Math.max(100, window.innerWidth * 0.25)}px` : '120px',
            height: isMobile ? `${Math.max(75, window.innerWidth * 0.19)}px` : '90px',
            bottom: isMobile && forceFullscreen ? '120px' : isMobile ? '100px' : '8px'
          }}
        >
          <TrackRefContext.Provider value={localVideoTrack}>
            <VideoTrack 
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          </TrackRefContext.Provider>
        </div>
      )}

      {/* Controls - on same level as personal camera */}
      <div 
        className={`absolute ${dir === 'rtl' ? 'left-2' : 'right-2'} z-30`}
        style={{
          bottom: isMobile && forceFullscreen ? '120px' : isMobile ? '100px' : '8px'
        }}
      >
        <VideoControls onEndCall={onEndCall} />
      </div>
    </div>
  );
}

export function FloatingVideoCall({
  roomId,
  userRole,
  userName,
  otherParticipantName,
  onCallEnded,
  open,
  appointmentDate,
  forceFullscreen = false,
  attempt,
  onRemoteJoined
}: FloatingVideoCallProps) {
  const { t, language, dir } = useLanguage();

  const [isMobile, setIsMobile] = useState(forceFullscreen || window.innerWidth < 768);
  
  // Floating window state (for desktop non-fullscreen mode)
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 280 });
  const [size, setSize] = useState({ width: 320, height: 240 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const windowRef = useRef<HTMLDivElement>(null);

  // Validate appointment date
  useEffect(() => {
    if (appointmentDate && !isToday(appointmentDate)) {
      toast.error(language === 'ar' ? 'المكالمات متاحة فقط في يوم الموعد' : 'Video calls can only be used on the appointment day');
      resetFavicon();
      onCallEnded(0, { connected: false, durationMinutes: 0 }); // No duration since call was not allowed
    }
  }, [appointmentDate]);

  // LiveKit-compatible room name (same rule as the mobile app's formatRoomName)
  const liveKitRoomName = roomId ? toLiveKitRoomName(roomId) : '';

  // Token fetch, connect timeout, retry, LiveKit errors, callAttempts (#47)
  const {
    token,
    loading,
    error,
    retry,
    finish,
    roomHandlers,
    attemptKey,
    watcherProps
  } = useLiveKitConnection({
    enabled: open,
    roomName: liveKitRoomName,
    // Ensure we have a valid participant name
    participantName: userName?.trim() || (userRole === 'coach' ? 'Coach' : 'Guest'),
    metadata: JSON.stringify({
      role: userRole,
      roomId,
      source: 'namat-floating'
    }),
    language,
    logPrefix: '[FloatingVideoCall]',
    attempt: attempt ? { ...attempt, role: userRole } : null,
    onRemoteJoined
  });

  // Window resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(forceFullscreen || window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [forceFullscreen]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    const target = e.target as HTMLElement;
    const isButton = target.tagName === 'BUTTON' || target.closest('button');
    const isResizeHandle = target.classList.contains('resize-handle');
    
    if (!isButton && !isResizeHandle) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
    if (isResizing) {
      const newWidth = Math.max(320, resizeStart.width + (e.clientX - resizeStart.x));
      const newHeight = Math.max(240, resizeStart.height + (e.clientY - resizeStart.y));
      setSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetFavicon();
    };
  }, []);

  const handleEndCall = () => {
    console.log('[FloatingVideoCall] Ending call');
    resetFavicon();
    
    // Duration counts from when the other party joined; 0 and not connected if they never did
    const outcome = finish();
    console.log('[FloatingVideoCall] Call outcome:', outcome);

    if (outcome.connected) {
      toast.success(language === 'ar' ? 'تم إنهاء المكالمة' : 'Call ended');
    }
    onCallEnded(outcome.durationMinutes, outcome);
  };

  if (!open) return null;

  // Loading state
  if (loading) {
    return (
      <div
        ref={windowRef}
        className={`fixed bg-black overflow-hidden z-[70] pointer-events-auto ${
          isMobile ? 'inset-0' : 'rounded-xl shadow-2xl border border-gray-700'
        }`}
        style={{
          left: !isMobile ? position.x : 0,
          top: !isMobile ? position.y : 0,
          width: !isMobile ? size.width : '100vw',
          height: !isMobile ? size.height : '100vh',
        }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-white mx-auto" />
            <p className="mt-3 text-white text-sm">
              {language === 'ar' ? 'جاري الاتصال...' : 'Connecting...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !token) {
    return (
      <div
        ref={windowRef}
        className={`fixed bg-black overflow-hidden z-[70] pointer-events-auto ${
          isMobile ? 'inset-0' : 'rounded-xl shadow-2xl border border-gray-700'
        }`}
        style={{
          left: !isMobile ? position.x : 0,
          top: !isMobile ? position.y : 0,
          width: !isMobile ? size.width : '100vw',
          height: !isMobile ? size.height : '100vh',
        }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-4">
            <p className="text-red-400 text-sm mb-3">{error || (language === 'ar' ? 'فشل الاتصال بالمكالمة' : 'Failed to connect to call')}</p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => { void retry(); }}
                className="px-4 py-2 bg-[#004250] text-white rounded-lg text-sm hover:bg-[#003240]"
              >
                {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
              </button>
              <button
                onClick={handleEndCall}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={windowRef}
      className={`fixed bg-black overflow-hidden z-[70] pointer-events-auto ${
        isMobile ? 'inset-0' : 'rounded-xl shadow-2xl border border-gray-700'
      }`}
      style={{
        left: !isMobile ? position.x : 0,
        top: !isMobile ? position.y : 0,
        width: !isMobile ? size.width : '100vw',
        height: !isMobile ? size.height : '100vh',
        cursor: isDragging ? 'grabbing' : (!isMobile ? 'move' : 'default')
      }}
      onMouseDown={handleMouseDown}
    >
      <LiveKitRoom
        key={attemptKey}
        video={true}
        audio={true}
        token={token}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        data-lk-theme="default"
        style={{ height: '100%', width: '100%' }}
        onConnected={roomHandlers.onConnected}
        onDisconnected={roomHandlers.onDisconnected}
        onError={roomHandlers.onError}
      >
        <LiveKitConnectionWatcher {...watcherProps} />
        <CustomVideoLayout 
          otherParticipantName={otherParticipantName} 
          language={language}
          dir={dir}
          isMobile={isMobile}
          forceFullscreen={forceFullscreen}
          onEndCall={handleEndCall}
          userRole={userRole}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>

      {/* Resize Handle - Desktop only */}
      {!isMobile && (
        <div
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeStart}
        />
      )}
    </div>
  );
}
